/**
 * /unit/:unitId/payment-links – every payment link for a unit.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { Archive, ArrowLeft, Copy, Download, ExternalLink, Gift, Link2, Loader2, Pencil, Plus, Power, QrCode, Search, Wallet } from 'lucide-react';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { db } from '@/lib/firebase';
import type { PaymentLink, PaymentLinkPayment } from '@masonic-tools/shared-types';
import { useCanManagePaymentLinks, useNow, usePaymentLinks, usePaymentLinkSetup } from '@/hooks/usePaymentLinks';
import {
  amountRuleLabel,
  feeModeShortLabel,
  formatDateTime,
  formatPence,
  linkDisplayState,
  linkMatchesTab,
  mapPaymentDoc,
  METHOD_PROVIDER_NAMES,
  relativeDays,
  type LinkListTab,
} from '@/lib/unit-payment-links';
import { PaymentLinkStatusBadge, ReferenceChip } from '@/components/payment-links/PaymentLinkStatusBadge';
import { PaymentLinkFormDialog } from '@/components/payment-links/PaymentLinkFormDialog';
import { copyLink, PaymentLinkQrDialog } from '@/components/payment-links/PaymentLinkQrDialog';
import { PaymentExportDialog } from '@/components/payment-links/PaymentExportDialog';
import { ActionsMenu, type ActionItem } from '@/components/payment-links/ActionsMenu';
import { confirmAndSetLinkStatus } from '@/components/payment-links/linkActions';
import { showError } from '@/lib/swal';

type SortOption = 'newest' | 'collected' | 'expiring';

const TABS: Array<{ value: LinkListTab; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

const EMPTY_TAB_TEXT: Record<LinkListTab, string> = {
  active: 'No active payment links.',
  expired: 'No payment links have expired.',
  disabled: 'No disabled payment links.',
  archived: 'Nothing archived.',
  all: 'No payment links yet.',
};

function StatCard({ title, value, sub, icon: Icon }: { title: string; value: string; sub?: string; icon: typeof Wallet }) {
  return (
    <Card className="min-w-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium leading-tight sm:text-sm">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="pt-0">
        <div className="text-xl font-bold tabular-nums sm:text-2xl">{value}</div>
        {sub ? <p className="text-[11px] text-muted-foreground sm:text-xs">{sub}</p> : null}
      </CardContent>
    </Card>
  );
}

export default function UnitPaymentLinksPage() {
  const { unitId = '' } = useParams<{ unitId: string }>();
  const navigate = useNavigate();
  const now = useNow();
  const canManage = useCanManagePaymentLinks(unitId);
  const setup = usePaymentLinkSetup(unitId);
  const { links, loading, error } = usePaymentLinks(canManage ? unitId : undefined);

  const [tab, setTab] = useState<LinkListTab>('active');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('newest');
  const [formOpen, setFormOpen] = useState(false);
  const [editLink, setEditLink] = useState<PaymentLink | null>(null);
  const [qrLink, setQrLink] = useState<PaymentLink | null>(null);
  const [exportData, setExportData] = useState<PaymentLinkPayment[] | null>(null);
  const [loadingExport, setLoadingExport] = useState(false);

  const counts = useMemo(() => {
    const c: Record<LinkListTab, number> = { active: 0, expired: 0, disabled: 0, archived: 0, all: 0 };
    for (const l of links) {
      const s = linkDisplayState(l, now, setup.loading ? undefined : setup.availableMethods);
      (Object.keys(c) as LinkListTab[]).forEach((t) => linkMatchesTab(s, t) && c[t]++);
    }
    return c;
  }, [links, now, setup.loading, setup.availableMethods]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = links.filter((l) => {
      const s = linkDisplayState(l, now, setup.loading ? undefined : setup.availableMethods);
      if (!linkMatchesTab(s, tab)) return false;
      return !q || l.title.toLowerCase().includes(q) || l.reference.toLowerCase().includes(q);
    });
    return [...filtered].sort((a, b) => {
      if (sort === 'collected') return b.totalCollectedPence - a.totalCollectedPence;
      if (sort === 'expiring') {
        const ea = a.expiresAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const eb = b.expiresAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
        return ea - eb;
      }
      return (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0);
    });
  }, [links, tab, search, sort, now, setup.loading, setup.availableMethods]);

  const stats = useMemo(() => {
    const monthStart = new Date(new Date(now).getFullYear(), new Date(now).getMonth(), 1).getTime();
    return {
      collected: links.reduce((s, l) => s + l.totalCollectedPence, 0),
      active: counts.active,
      giftAid: links.reduce((s, l) => s + l.giftAidEligiblePence, 0),
      giftAidCount: links.reduce((s, l) => s + l.giftAidCount, 0),
      thisMonthLinks: links.filter((l) => (l.lastPaymentAt?.getTime() ?? 0) >= monthStart).length,
    };
  }, [links, counts.active, now]);

  const linkTitles = useMemo(() => Object.fromEntries(links.map((l) => [l.id, l.title])), [links]);

  const exportAll = async () => {
    setLoadingExport(true);
    try {
      const all = await Promise.all(
        links.map(async (l) => {
          const snap = await getDocs(collection(db, 'units', unitId, 'paymentLinks', l.id, 'payments'));
          return snap.docs.map((d) => mapPaymentDoc(d.id, unitId, l.id, d.data()));
        }),
      );
      setExportData(all.flat().sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)));
    } catch {
      showError('Could not load payments for export');
    } finally {
      setLoadingExport(false);
    }
  };

  const actionsFor = (link: PaymentLink): ActionItem[] => [
    { label: 'Open', icon: ExternalLink, onSelect: () => navigate(`/unit/${unitId}/payment-links/${link.id}`) },
    { label: 'Copy link', icon: Copy, onSelect: () => copyLink(link.shortUrl) },
    { label: 'QR code', icon: QrCode, onSelect: () => setQrLink(link) },
    { label: 'Edit', icon: Pencil, onSelect: () => setEditLink(link), hidden: link.status === 'archived' },
    { label: 'Disable', icon: Power, onSelect: () => void confirmAndSetLinkStatus(link, 'disabled'), hidden: link.status !== 'active' },
    { label: 'Enable', icon: Power, onSelect: () => void confirmAndSetLinkStatus(link, 'active'), hidden: link.status === 'active' },
    { label: 'Archive', icon: Archive, onSelect: () => void confirmAndSetLinkStatus(link, 'archived'), destructive: true, hidden: link.status === 'archived' },
  ];

  if (!canManage) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Header />
        <main className="flex-1 py-8">
          <DashboardContentContainer>
            <Alert variant="warning">
              <AlertDescription>Only a Steward, Secretary or Treasurer can manage payment links for this unit.</AlertDescription>
            </Alert>
            <Button className="mt-4" variant="outline" onClick={() => navigate(`/unit/${unitId}/dashboard`)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to unit dashboard
            </Button>
          </DashboardContentContainer>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header />
      <main className="flex w-full min-w-0 flex-1 overflow-x-hidden py-6 sm:py-8">
        <DashboardContentContainer>
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <Button variant="ghost" size="sm" className="mb-2 px-3" onClick={() => navigate(`/unit/${unitId}/dashboard`)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to unit dashboard
              </Button>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Payment Links</h1>
              <p className="text-sm text-muted-foreground">{setup.unitName}</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={exportAll} disabled={loadingExport || links.length === 0}>
                {loadingExport ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export all links
              </Button>
              <Button onClick={() => setFormOpen(true)}>
                <Plus className="mr-2 h-4 w-4" /> New Payment Link
              </Button>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <StatCard title="Total collected" value={formatPence(stats.collected)} sub="All payment links" icon={Wallet} />
            <StatCard title="Active links" value={String(stats.active)} sub="Taking payments now" icon={Link2} />
            <StatCard title="Paid this month" value={String(stats.thisMonthLinks)} sub="Links with a payment this month" icon={Wallet} />
            <StatCard title="Gift Aid eligible" value={formatPence(stats.giftAid)} sub={`${stats.giftAidCount} declaration${stats.giftAidCount === 1 ? '' : 's'}`} icon={Gift} />
          </div>

          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="-mx-1 flex overflow-x-auto px-1" role="tablist" aria-label="Filter payment links">
              <div className="inline-flex rounded-md bg-muted p-1">
                {TABS.map((t) => (
                  <button
                    key={t.value}
                    role="tab"
                    aria-selected={tab === t.value}
                    onClick={() => setTab(t.value)}
                    className={cn(
                      'whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-colors',
                      tab === t.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {t.label} <span className="ml-1 text-xs text-muted-foreground">{counts[t.value]}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative sm:w-64">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" placeholder="Search title or reference" aria-label="Search payment links" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
                <SelectTrigger className="sm:w-44" aria-label="Sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="collected">Most collected</SelectItem>
                  <SelectItem value="expiring">Expiring soon</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertDescription>Could not load payment links: {error}</AlertDescription>
            </Alert>
          ) : loading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center py-10 text-center">
                <Link2 className="mb-3 h-10 w-10 text-muted-foreground" />
                <p className="font-medium">{search ? `No links match “${search}”` : links.length === 0 ? 'No payment links yet' : EMPTY_TAB_TEXT[tab]}</p>
                {links.length === 0 ? (
                  <Button className="mt-4" onClick={() => setFormOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" /> Create your first payment link
                  </Button>
                ) : search ? (
                  <Button variant="link" onClick={() => setSearch('')}>
                    Clear search
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Desktop */}
              <div className="hidden overflow-visible rounded-lg border border-border md:block">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Payment link</th>
                      <th className="px-3 py-2 font-medium">Amount</th>
                      <th className="px-3 py-2 font-medium">Method</th>
                      <th className="px-3 py-2 font-medium">Fee</th>
                      <th className="px-3 py-2 text-right font-medium">Collected</th>
                      <th className="px-3 py-2 font-medium">Expiry</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((link) => (
                      <tr
                        key={link.id}
                        className="cursor-pointer border-t border-border hover:bg-muted/40"
                        onClick={() => navigate(`/unit/${unitId}/payment-links/${link.id}`)}
                      >
                        <td className="max-w-[280px] px-3 py-2">
                          <div className="truncate font-medium">{link.title}</div>
                          <ReferenceChip reference={link.reference} className="mt-1" />
                        </td>
                        <td className="px-3 py-2">{amountRuleLabel(link)}</td>
                        <td className="px-3 py-2">{METHOD_PROVIDER_NAMES[link.paymentMethod]}</td>
                        <td className="px-3 py-2 text-muted-foreground">{feeModeShortLabel(link.feeMode)}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="font-medium tabular-nums">{formatPence(link.totalCollectedPence)}</div>
                          <div className="text-xs text-muted-foreground">
                            {link.paymentCount} payment{link.paymentCount === 1 ? '' : 's'}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground" title={link.expiresAt ? formatDateTime(link.expiresAt) : undefined}>
                          {link.expiresAt ? relativeDays(link.expiresAt.getTime(), now) : 'No expiry'}
                        </td>
                        <td className="px-3 py-2">
                          <PaymentLinkStatusBadge link={link} nowMs={now} availableMethods={setup.loading ? undefined : setup.availableMethods} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <ActionsMenu items={actionsFor(link)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile */}
              <ul className="space-y-2 md:hidden">
                {visible.map((link) => (
                  <li key={link.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/unit/${unitId}/payment-links/${link.id}`)}
                      onKeyDown={(e) => e.key === 'Enter' && navigate(`/unit/${unitId}/payment-links/${link.id}`)}
                      className="rounded-lg border border-border p-3 hover:bg-muted/40"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate font-medium">{link.title}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <ReferenceChip reference={link.reference} />
                            <span>{amountRuleLabel(link)}</span>
                            <span>· {METHOD_PROVIDER_NAMES[link.paymentMethod]}</span>
                          </div>
                        </div>
                        <ActionsMenu items={actionsFor(link)} />
                      </div>
                      <div className="mt-2 flex items-end justify-between gap-2">
                        <div>
                          <span className="text-lg font-bold tabular-nums">{formatPence(link.totalCollectedPence)}</span>
                          <span className="ml-1 text-xs text-muted-foreground">from {link.paymentCount}</span>
                        </div>
                        <PaymentLinkStatusBadge link={link} nowMs={now} availableMethods={setup.loading ? undefined : setup.availableMethods} />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </DashboardContentContainer>
      </main>

      <PaymentLinkFormDialog unitId={unitId} open={formOpen} onOpenChange={setFormOpen} setup={setup} />
      <PaymentLinkFormDialog unitId={unitId} open={!!editLink} onOpenChange={(o) => !o && setEditLink(null)} setup={setup} link={editLink} />
      <PaymentLinkQrDialog link={qrLink} unitName={setup.unitName} open={!!qrLink} onOpenChange={(o) => !o && setQrLink(null)} />
      {exportData ? (
        <PaymentExportDialog
          open
          onOpenChange={(o) => !o && setExportData(null)}
          unitId={unitId}
          unitName={setup.unitName}
          title="All payment links"
          reference="payment-links"
          allPayments={exportData}
          linkTitles={linkTitles}
        />
      ) : null}
    </div>
  );
}
